import { Business } from '@/components/locations/types';
import { captureException } from '@/utils/sentry';
import { CREATE_RESTAURANT_MUTATION } from '@/screens/locations/create-account-screen';
import {
  CreationSourceFeatures,
  LeadGenerator_NearbyBusinessesQuery,
  LeadGenerator_NearbyBusinessesQueryVariables,
  Locations_CreateAccountMutation,
  Locations_CreateAccountMutationVariables,
} from '@/types';
import {
  GET_NEARBY_BUSINESSES,
  NearbyBusinessFilters,
  PAGE_SIZE,
  STARTING_OFFSET,
} from '@/hooks/use-nearby-businesses';
import { getOperationName } from 'apollo-utilities';
import { useCallback, useMemo, useState } from 'react';
import { useMutation } from '@apollo/client';
import { USER_QUERY } from '@/contexts/user-context-queries';
import { useToasts } from '@/contexts/toast-context';

export const useLeadGeneratorCreateAccount = (
  supplierUUID: string,
  {
    categories,
    isCurrentRestaurant,
    price,
    sortBy,
    latitude,
    longitude,
  }: NearbyBusinessFilters,
  onAccountCreated: (businessUUID: string, businessName: string) => void,
) => {
  const { showErrorToast } = useToasts();

  const [newAccountYelpBusinessID, setNewAccountYelpBusinessID] = useState<
    string | null
  >(null);

  const [createAccountMutation] = useMutation<
    Locations_CreateAccountMutation,
    Locations_CreateAccountMutationVariables
  >(CREATE_RESTAURANT_MUTATION, {
    awaitRefetchQueries: false,
    refetchQueries: [getOperationName(USER_QUERY) as string],
    update: (client, { data: createAccountData }) => {
      try {
        const nearbyBusinessesQueryVariables: LeadGenerator_NearbyBusinessesQueryVariables =
          {
            categories,
            isCurrentRestaurant,
            latitude,
            limit: PAGE_SIZE,
            longitude,
            offset: STARTING_OFFSET,
            price,
            sortBy,
            supplierUUID,
          };
        const cachedNearbyBusinesses: LeadGenerator_NearbyBusinessesQuery | null =
          client.readQuery({
            query: GET_NEARBY_BUSINESSES,
            variables: nearbyBusinessesQueryVariables,
          });
        if (cachedNearbyBusinesses === null) {
          return;
        }
        if (isCurrentRestaurant === false) {
          // Remove business from list
          const updatedNearbyBusinesses =
            cachedNearbyBusinesses.getNearbyBusinessesV2.filter(
              (b) =>
                b.business_name !==
                createAccountData?.supplierCreateRestaurant.business?.name,
            );

          client.writeQuery<LeadGenerator_NearbyBusinessesQuery>({
            data: {
              ...cachedNearbyBusinesses!,
              getNearbyBusinessesV2: updatedNearbyBusinesses,
            },
            query: GET_NEARBY_BUSINESSES,
            variables: nearbyBusinessesQueryVariables,
          });
        } else {
          // Update business with correct UUID
          const newBusinessName =
            createAccountData?.supplierCreateRestaurant.business?.name;
          const newBusinessUUID =
            createAccountData?.supplierCreateRestaurant.business?.uuid;
          const updatedNearbyBusinesses =
            cachedNearbyBusinesses.getNearbyBusinessesV2.map((b) =>
              b.business_name === newBusinessName
                ? { ...b, restaurant_uuid: newBusinessUUID }
                : b,
            );

          client.writeQuery<LeadGenerator_NearbyBusinessesQuery>({
            data: {
              ...cachedNearbyBusinesses!,
              getNearbyBusinessesV2: updatedNearbyBusinesses,
            },
            query: GET_NEARBY_BUSINESSES,
            variables: nearbyBusinessesQueryVariables,
          });
        }
      } catch (e) {
        captureException(e);
      }
    },
  });

  const createAccount = useCallback(
    async (business: Business) => {
      try {
        setNewAccountYelpBusinessID(business.yelp_business_id);
        const { data: createAccountData } = await createAccountMutation({
          variables: {
            accountID: null,
            address: business.business_display_address,
            address2: null,
            creationSourceFeature: CreationSourceFeatures.LeadGenerator,
            metadata: {
              cuisine: business.categories ? business.categories[0] : undefined,
              display_phone_number: business.phone_number,
              yelp_business_id: business.yelp_business_id,
            },
            name: business.business_name,
            notes: null,
            supplierUUID,
          },
        });
        if (createAccountData?.supplierCreateRestaurant.business?.uuid) {
          onAccountCreated(
            createAccountData.supplierCreateRestaurant.business.uuid,
            business.business_name,
          );
          setNewAccountYelpBusinessID(null);
        } else {
          showErrorToast();
          setNewAccountYelpBusinessID(null);
        }
      } catch (e) {
        showErrorToast();
        setNewAccountYelpBusinessID(null);
      }
    },
    [createAccountMutation, onAccountCreated, showErrorToast, supplierUUID],
  );

  return useMemo(
    () => ({
      createAccount,
      newAccountYelpBusinessID,
      setNewAccountYelpBusinessID,
    }),
    [createAccount, newAccountYelpBusinessID, setNewAccountYelpBusinessID],
  );
};
